import { Component } from "react";
import { isChunkLoadError, recoverFromChunkError } from "../lib/chunkRecovery";

/**
 * Catches render/lifecycle exceptions so a single bad field never blanks the
 * whole app. React unmounts the entire tree when an error escapes, which is
 * what produced the white screen on /admin/products.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // A tab open across a deploy fails on the first stale chunk it needs; one
    // reload picks up the current build instead of stranding the user here.
    if (recoverFromChunkError(error)) return;
    console.error("Uncaught runtime error:", error, errorInfo);
    this.setState({ componentStack: errorInfo?.componentStack || "" });
  }

  componentDidUpdate(prevProps) {
    // `resetKey` is normally the route path, so navigating away clears the
    // fallback instead of leaving the admin stuck on the error card.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, componentStack: "" });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, componentStack } = this.state;
    const staleAssets = isChunkLoadError(error);
    const message = staleAssets
      ? "This page is running an older version of the app. Reload to pick up the latest build."
      : error?.message || "An unexpected error occurred.";

    return (
      <div
        className="min-h-screen bg-slate-50 px-4 py-12"
        data-testid="error-boundary-fallback"
      >
        <div className="mx-auto max-w-lg rounded-lg border border-red-200 bg-red-50 p-8">
          <h2 className="mb-2 text-xl font-bold text-red-700">
            {staleAssets ? "Update Available" : "Application Error"}
          </h2>
          <p className="mb-4 text-sm text-red-600">{message}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              data-testid="error-boundary-reload"
            >
              Reload Page
            </button>
            <a
              href="/admin"
              className="rounded border border-red-300 bg-white px-4 py-2 text-red-700 hover:bg-red-100"
            >
              Back to Admin
            </a>
          </div>
          {componentStack ? (
            <details className="mt-6">
              <summary className="cursor-pointer text-xs font-medium text-red-700">
                Technical details
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-white p-3 text-[11px] leading-relaxed text-slate-600">
                {`${error?.stack || message}\n${componentStack}`}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}
